import { motion } from "framer-motion";
import { MapPin, ArrowDown, Star } from "lucide-react";
import { waLink, imgUrl } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1728910156510-77488f19b152?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

function MaskLine({ children, i }) {
  return (
    <span className="block overflow-hidden">
      <motion.span className="block" variants={line} custom={i} initial="hidden" animate="show">
        {children}
      </motion.span>
    </span>
  );
}

export function Hero({ onCalc, onQuote, summary, images }) {
  const heroImg = images?.hero ? imgUrl(images.hero) : HERO_IMG;
  return (
    <section id="top" className="relative pt-10 pb-10 px-5 overflow-hidden">
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-2 text-primary mb-5"
        >
          <MapPin className="w-4 h-4" />
          <span className="text-xs tracking-[0.25em] uppercase font-semibold">Kanpur • Est. Fine Catering</span>
        </motion.div>

        <h1 className="font-serif text-[2.6rem] leading-[0.98] sm:text-6xl lg:text-7xl tracking-tight text-primary">
          <MaskLine i={0}>Bulk Food</MaskLine>
          <MaskLine i={1}>& Catering</MaskLine>
          <MaskLine i={2}>
            <span className="italic text-accent">in Kanpur</span>
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mt-6 max-w-md text-base sm:text-lg text-muted-foreground leading-relaxed"
        >
          Freshly prepared cloud-kitchen feasts and full-service event catering — priced honestly, delivered on time.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.6 }}
          className="mt-7 flex flex-col sm:flex-row gap-3"
        >
          <button
            data-testid="hero-calc-btn"
            onClick={onCalc}
            className="h-14 px-8 rounded-full bg-primary text-primary-foreground font-medium text-base active:scale-[0.98] transition-transform"
          >
            Calculate Bulk Food Price
          </button>
          <a
            data-testid="hero-quote-btn"
            href={waLink("Hi The Quisine! I'd like a catering quote.")}
            target="_blank"
            rel="noreferrer"
            onClick={onQuote}
            className="h-14 px-8 rounded-full border border-primary text-primary font-medium text-base grid place-items-center active:scale-[0.98] transition-transform"
          >
            Get Catering Quote
          </a>
        </motion.div>

        {summary && summary.total > 0 && (
          <motion.a
            href={summary.review_link || undefined}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-rating-badge"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-primary/15 bg-card px-4 py-2 shadow-sm"
          >
            <span className="flex items-center gap-0.5">
              {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-4 h-4 text-accent fill-current" />)}
            </span>
            <span className="text-sm text-foreground/80"><strong className="text-primary">{summary.rating}</strong> · {summary.total}+ Google ratings</span>
          </motion.a>
        )}

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="mt-10 relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden aspect-[4/3] sm:aspect-[16/9]">
            <img
              src={heroImg}
              alt="Premium Indian catering spread"
              fetchPriority="high"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
              <div className="text-primary-foreground">
                <div className="font-serif text-2xl leading-none">500+ events</div>
                <div className="text-xs opacity-80 mt-1">catered across Kanpur</div>
              </div>
              <div className="grid place-items-center w-10 h-10 rounded-full bg-primary-foreground/90 text-primary animate-bounce">
                <ArrowDown className="w-4 h-4" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
